import React from 'react';
import { Button, Text, Flex, Spinner } from '@chatsdet/ui';
import { CheckCircle, XCircle, Stethoscope } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import type { AgentEvent } from '../../hooks/useApiSequenceGenerationStream';

export type DoctorMode = 'fix' | 'improve';

export type DoctorState = {
  status: 'idle' | 'running' | 'complete' | 'error';
  mode: DoctorMode;
  events: AgentEvent[];
  summary: string | null;
  onRunDoctor: () => void;
  onApplyFix: () => void;
  onCancel: () => void;
  onDismiss: () => void;
  isApplying: boolean;
};

/** Visible event types to show in the progress log */
const VISIBLE_EVENT_TYPES = new Set(['progress', 'text', 'tool-call', 'tool-result']);

function progressLabel(event: AgentEvent): string | null {
  if (event.type === 'progress' || event.type === 'text') {
    return event.message ?? event.delta ?? null;
  }
  if (event.type === 'tool-call' && event.toolName) {
    return `Calling ${event.toolName}...`;
  }
  if (event.type === 'tool-result' && event.toolName) {
    return `${event.toolName} ${event.success ? 'succeeded' : 'failed'}`;
  }
  return null;
}

export const ApiSequenceDoctorView: React.FC<{ doctor: DoctorState }> = ({ doctor }) => {
  const logRef = React.useRef<HTMLDivElement>(null);
  const visibleEvents = doctor.events.filter((e) => VISIBLE_EVENT_TYPES.has(e.type));
  const isImprove = doctor.mode === 'improve';

  // Auto-scroll progress log
  React.useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [visibleEvents.length]);

  // Applying state
  if (doctor.isApplying) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Spinner size="lg" />
        <Text size="sm" className="text-muted-foreground">
          {isImprove ? 'Applying improvements and re-running sequence...' : 'Applying fix and re-running sequence...'}
        </Text>
      </div>
    );
  }

  // Running state
  if (doctor.status === 'running') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Spinner size="sm" />
          <Text size="sm" weight="medium">
            {isImprove ? 'Analyzing sequence...' : 'Diagnosing failures...'}
          </Text>
        </div>
        <div ref={logRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
          <AnimatePresence>
            {visibleEvents.map((event, i) => {
              const label = progressLabel(event);
              if (!label) return null;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Text size="xs" className="text-muted-foreground font-mono">
                    {label}
                  </Text>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
        <div className="px-4 py-3 border-t border-border">
          <Button variant="ghost" size="sm" onPress={doctor.onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Complete state
  if (doctor.status === 'complete') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <Flex align="center" gap="sm">
            <CheckCircle size={20} weight="fill" className="text-green-500 shrink-0" />
            <Text size="sm" weight="semibold">
              {isImprove ? 'Analysis Complete' : 'Diagnosis Complete'}
            </Text>
          </Flex>
          {doctor.summary && (
            <div className="rounded-md bg-muted/50 border border-border px-3 py-2.5">
              <Text size="xs" className="text-foreground whitespace-pre-wrap leading-relaxed">
                {doctor.summary}
              </Text>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-border">
          <Flex gap="sm">
            <Button variant="primary" size="sm" onPress={doctor.onApplyFix}>
              <Stethoscope size={14} />
              {isImprove ? 'Apply Improvements' : 'Apply Fix'}
            </Button>
            <Button variant="ghost" size="sm" onPress={doctor.onDismiss}>
              Dismiss
            </Button>
          </Flex>
        </div>
      </div>
    );
  }

  // Error state
  if (doctor.status === 'error') {
    const lastError = [...doctor.events].reverse().find((e) => e.type === 'error');
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <Flex align="center" gap="sm">
            <XCircle size={20} weight="fill" className="text-red-500 shrink-0" />
            <Text size="sm" weight="semibold">
              {isImprove ? 'Analysis Failed' : 'Diagnosis Failed'}
            </Text>
          </Flex>
          {lastError?.message && (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2.5">
              <Text size="xs" className="text-destructive">
                {lastError.message}
              </Text>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-border">
          <Flex gap="sm">
            <Button variant="secondary" size="sm" onPress={doctor.onRunDoctor}>
              Try Again
            </Button>
            <Button variant="ghost" size="sm" onPress={doctor.onDismiss}>
              Dismiss
            </Button>
          </Flex>
        </div>
      </div>
    );
  }

  // idle — shouldn't render this component, but just in case
  return null;
};
